// src/components/GlobalFunction/AuthErrorMessage.jsx
import React, { useMemo } from "react";
import { Alert, Typography } from "@mui/material";

/**
 * Decode HTML entities safely (works in browser + fallback for non-DOM env).
 */
function decodeHtmlEntities(input) {
  const s = String(input ?? "");
  if (!s) return "";

  // Browser DOM decode (best)
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const txt = document.createElement("textarea");
    txt.innerHTML = s;
    return txt.value || "";
  }

  // Fallback decode for common entities
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&#x0*27;/gi, "'");
}

/**
 * Strip tags + collapse whitespace + remove common WP boilerplate.
 * This turns WP error HTML into a readable plain string.
 */
function sanitizeWpHtmlLikeMessage(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return "";

  // Decode entities first
  s = decodeHtmlEntities(s);

  // Remove scripts/styles just in case
  s = s.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, " ");
  s = s.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, " ");

  // Replace <br> with space
  s = s.replace(/<br\s*\/?>/gi, " ");

  // Strip remaining tags
  s = s.replace(/<\/?[^>]+>/g, " ");

  // Remove common WP prefixes
  s = s.replace(/\bError:\b/gi, " ");
  s = s.replace(/\bERROR:\b/gi, " ");

  // Remove "Lost your password?" text (we don't want to show the link)
  s = s.replace(/Lost your password\?/gi, " ");
  s = s.replace(/パスワードをお忘れですか\?/gi, " ");
  s = s.replace(/忘記密碼\？/g, " ");

  // Remove URLs
  s = s.replace(/https?:\/\/\S+/gi, " ");

  // Collapse whitespace
  s = s.replace(/\s+/g, " ").trim();

  return s;
}

/**
 * Extract a readable message from GraphQL / REST / thrown errors.
 */
export function extractWpErrorMessage(err) {
  // If a plain string was passed in
  if (typeof err === "string") {
    return sanitizeWpHtmlLikeMessage(err);
  }

  const direct = err?.message;
  if (direct) return sanitizeWpHtmlLikeMessage(String(direct).trim());

  const gqlMsg =
    err?.graphQLErrors?.[0]?.message ||
    err?.response?.errors?.[0]?.message ||
    err?.response?.data?.message;

  if (gqlMsg) return sanitizeWpHtmlLikeMessage(String(gqlMsg).trim());

  const any = err?.toString?.();
  return any ? sanitizeWpHtmlLikeMessage(String(any).trim()) : "";
}

function normLang(code) {
  const s = String(code || "").toUpperCase().trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

function pickLang(lang, dict) {
  const l = normLang(lang);
  return dict?.[l] ?? dict?.ZH ?? "";
}

/**
 * Normalize server error strings to "buckets" we want to show.
 */
function classifyError(rawMsg) {
  const cleaned = sanitizeWpHtmlLikeMessage(rawMsg);
  const m = String(cleaned || "").trim().toLowerCase();
  if (!m) return { code: "UNKNOWN" };

  // OTP / rate limits
  if (
    m.includes("rate") ||
    m.includes("too many") ||
    m.includes("too_many") ||
    m.includes("throttle") ||
    m.includes("attempt") ||
    m.includes("limit")
  ) {
    if (m.includes("otp") || m.includes("verification code") || m.includes("code")) {
      return { code: "OTP_RATE_LIMITED" };
    }
    return { code: "RATE_LIMITED" };
  }

  if (m.includes("otp") && (m.includes("expired") || m.includes("timeout") || m.includes("time out"))) {
    return { code: "OTP_EXPIRED" };
  }
  if (m.includes("expired otp") || m.includes("code expired") || m.includes("code expired or not found"))
    return { code: "OTP_EXPIRED" };

  if (m.includes("otp") && (m.includes("invalid") || m.includes("wrong") || m.includes("mismatch"))) {
    return { code: "OTP_INVALID" };
  }
  if (m.includes("invalid otp") || m.includes("invalid code")) return { code: "OTP_INVALID" };

  if (m.includes("otp required") || (m.includes("otp") && m.includes("required")) || m.includes("email verification required")) {
    return { code: "OTP_REQUIRED" };
  }

  // "already registered" buckets
  if (m.includes("email") && (m.includes("already registered") || m.includes("already exists") || m.includes("taken"))) {
    return { code: "EMAIL_TAKEN" };
  }
  if (m.includes("username") && (m.includes("already registered") || m.includes("already exists") || m.includes("taken"))) {
    return { code: "USERNAME_TAKEN" };
  }
  if (
    (m.includes("phone") || m.includes("mobile")) &&
    (m.includes("already registered") || m.includes("already exists") || m.includes("taken"))
  ) {
    return { code: "PHONE_TAKEN" };
  }

  // classic wrong password / login failure messages
  if (
    m.includes("the password you entered for the username") ||
    (m.includes("password") && m.includes("is incorrect")) ||
    m.includes("invalid username") ||
    m.includes("unknown username") ||
    m.includes("invalid email") ||
    m.includes("invalid credentials")
  ) {
    return { code: "INVALID_CREDENTIALS" };
  }

  if (
    m.includes("incorrect") ||
    (m.includes("invalid") && m.includes("password")) ||
    (m.includes("wrong") && m.includes("password"))
  ) {
    return { code: "INVALID_CREDENTIALS" };
  }

  if (m.includes("old password") && (m.includes("wrong") || m.includes("incorrect") || m.includes("invalid"))) {
    return { code: "OLD_PASSWORD_INCORRECT" };
  }

  if (m.includes("session") || m.includes("jwt") || m.includes("token") || m.includes("expired")) {
    return { code: "SESSION_EXPIRED" };
  }

  // validation
  if (m.includes("invalid taiwan phone") || (m.includes("taiwan") && m.includes("phone"))) {
    return { code: "INVALID_TW_PHONE" };
  }
  if (m.includes("email") && m.includes("invalid")) return { code: "INVALID_EMAIL" };

  // GraphQL schema mismatch (like your birthdate/gender issue)
  if (m.includes("field") && m.includes("is not defined by type")) {
    return { code: "GQL_SCHEMA_MISMATCH" };
  }

  return { code: "UNKNOWN" };
}

/**
 * Map error -> localized UI string
 */
export function mapWpErrorToUiMessage({ rawMsg, lang, t, fallbackKey }) {
  const msg = sanitizeWpHtmlLikeMessage(rawMsg);
  const { code } = classifyError(msg);

  const tryT = (key, fallback) => {
    if (typeof t === "function") {
      const out = t(key);
      if (out && out !== key) return out;
    }
    return fallback;
  };

  const builtIn = {
    OTP_RATE_LIMITED: pickLang(lang, {
      ZH: "嘗試次數過多，請稍後再試。",
      EN: "Too many attempts. Please try again later.",
      JP: "試行回数が多すぎます。しばらくしてから再試行してください。",
    }),
    RATE_LIMITED: pickLang(lang, {
      ZH: "操作太頻繁，請稍後再試。",
      EN: "Too many requests. Please try again later.",
      JP: "リクエストが多すぎます。しばらくしてから再試行してください。",
    }),
    OTP_EXPIRED: tryT(
      "otpExpired",
      pickLang(lang, {
        ZH: "驗證碼已過期，請重新寄送。",
        EN: "Code expired. Please resend.",
        JP: "コードの有効期限が切れました。再送してください。",
      })
    ),
    OTP_INVALID: tryT(
      "otpInvalid",
      pickLang(lang, {
        ZH: "驗證碼無效，請確認後再試。",
        EN: "Invalid code. Please try again.",
        JP: "コードが無効です。確認して再試行してください。",
      })
    ),
    OTP_REQUIRED: tryT(
      "otpRequired",
      pickLang(lang, {
        ZH: "需要驗證碼，請先寄送並輸入驗證碼。",
        EN: "Verification code is required. Please send and enter it.",
        JP: "認証コードが必要です。送信して入力してください。",
      })
    ),
    EMAIL_TAKEN: tryT(
      "emailTaken",
      pickLang(lang, {
        ZH: "此電子郵件已被註冊。",
        EN: "This email is already registered.",
        JP: "このメールアドレスは既に登録されています。",
      })
    ),
    USERNAME_TAKEN: tryT(
      "usernameTaken",
      pickLang(lang, {
        ZH: "此用戶名稱已被使用。",
        EN: "This username is already taken.",
        JP: "このユーザー名は既に使用されています。",
      })
    ),
    PHONE_TAKEN: tryT(
      "phoneTaken",
      pickLang(lang, {
        ZH: "此手機號碼已被註冊。",
        EN: "This phone number is already registered.",
        JP: "この電話番号は既に登録されています。",
      })
    ),
    INVALID_CREDENTIALS: tryT(
      "invalidCredentials",
      pickLang(lang, {
        ZH: "用戶名稱或密碼無效。",
        EN: "Invalid username or password.",
        JP: "ユーザー名またはパスワードが無効です。",
      })
    ),
    OLD_PASSWORD_INCORRECT: pickLang(lang, {
      ZH: "舊密碼不正確。",
      EN: "Old password is incorrect.",
      JP: "現在のパスワードが正しくありません。",
    }),
    SESSION_EXPIRED: tryT(
      "sessionExpired",
      pickLang(lang, {
        ZH: "登入已失效，請重新登入。",
        EN: "Session expired. Please log in again.",
        JP: "ログイン情報が無効です。再ログインしてください。",
      })
    ),
    INVALID_TW_PHONE: tryT(
      "signupPhoneFormatError",
      pickLang(lang, {
        ZH: "手機號碼格式需為台灣 09XXXXXXXX。",
        EN: "Phone number must be a Taiwan number in 09XXXXXXXX format.",
        JP: "電話番号は台湾の 09XXXXXXXX 形式で入力してください。",
      })
    ),
    INVALID_EMAIL: pickLang(lang, {
      ZH: "電子郵件格式不正確。",
      EN: "Invalid email format.",
      JP: "メールアドレスの形式が正しくありません。",
    }),
    GQL_SCHEMA_MISMATCH: pickLang(lang, {
      ZH: "系統欄位不相容（GraphQL schema mismatch）。請更新後端欄位定義。",
      EN: "Schema mismatch (GraphQL input type). Backend must be updated.",
      JP: "スキーマ不一致（GraphQL入力型）。バックエンドの更新が必要です。",
    }),
    UNKNOWN: "",
  };

  if (builtIn[code]) return builtIn[code];

  // last fallback: keep sanitized server message if it's short
  const safeMsg = msg && msg.length <= 180 ? msg : "";
  if (safeMsg) return safeMsg;

  if (typeof t === "function" && fallbackKey) return t(fallbackKey);

  return pickLang(lang, {
    ZH: "操作失敗，請稍後再試。",
    EN: "Action failed. Please try again later.",
    JP: "操作に失敗しました。しばらくしてから再試行してください。",
  });
}

/**
 * Convenience helper:
 * Takes "anything" (Error/string/graphql error) => returns final UI string.
 */
export function toUiAuthError(err, { lang = "ZH", t } = {}) {
  const raw = extractWpErrorMessage(err);
  return mapWpErrorToUiMessage({ rawMsg: raw, lang, t });
}

/**
 * Global UI component to display an error message consistently.
 *
 * Backward compatible props:
 * - text (preferred)
 * - error (legacy)
 */
export function AuthErrorMessage({
  text,
  error, // legacy prop name
  variant = "text", // "text" | "alert"
  severity = "error",
  sx,
}) {
  const msg = String(text || error || "").trim();
  if (!msg) return null;

  if (variant === "alert") {
    return (
      <Alert severity={severity} variant="filled" sx={sx}>
        {msg}
      </Alert>
    );
  }

  return (
    <Typography color="error" variant="body2" sx={sx}>
      {msg}
    </Typography>
  );
}
